'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import {
  isNativePlatform,
  isIOS,
  isAndroid,
  getPlatform,
  PlatformInfo,
  getPlatformInfo,
} from '@/lib/capacitor/platform';
import {
  initializeMobileAuth,
  onMobileLogin,
  onMobileLogout,
  cleanupMobileAuth,
} from '@/lib/capacitor/auth-handler';
import {
  initializeDeepLinks,
  getInsuraCentralRoutes,
  getLaunchUrl,
} from '@/lib/capacitor/deep-links';
import { initializeKeyboard, hideKeyboard } from '@/lib/capacitor/keyboard';
import { runPostLoginPermissionFlow, hasCompletedPostLoginPermissions, ensureAnalyticsConsent } from '@/lib/capacitor/post-login-permissions';
import { setStatusBarStyle, setStatusBarOverlay } from '@/lib/capacitor/safe-area';
import {
  WebVersionModal,
  WebVersionFeature,
} from './WebVersionModal';

/**
 * Hide the native splash screen when web content is ready
 * We use a quick fade so the web-based MobileSplashScreen can take over smoothly
 */
async function hideSplashScreen(): Promise<void> {
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    // Quick fade to hand off to web splash screen
    await SplashScreen.hide({ fadeOutDuration: 150 });
    console.log('[MobileProvider] Native splash screen hidden');
  } catch (error) {
    // Splash screen plugin not available or already hidden
    console.log('Splash screen hide:', error);
  }
}

// Global flag to coordinate splash screen handoff
// MobileSplashScreen will set this to true when it's ready
declare global {
  interface Window {
    __mobileSplashReady?: boolean;
  }
}

// Lazy load axios to avoid SSR issues
const getAxiosClient = async () => {
  const { default: axios } = await import('axios');
  const { getApiUrl } = await import('@/app/_module/services/config');
  const { default: supabaseAuthService } = await import('@/app/_module/services/supabaseAuthService');

  const instance = axios.create({
    baseURL: getApiUrl(''),
    timeout: 30000,
    headers: {
      Accept: 'application/json',
    },
  });

  // Add auth interceptor
  instance.interceptors.request.use(async (config) => {
    const token = await supabaseAuthService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add mobile client identifier
    config.headers['X-Client-Info'] = 'insuracentral-mobile';
    return config;
  });

  return {
    post: (url: string, data: any, config?: any) => instance.post(url, data, config),
    put: (url: string, data: any, config?: any) => instance.put(url, data, config),
    delete: (url: string, config?: any) => instance.delete(url, config),
  };
};

interface MobileContextValue {
  // Platform info
  platformInfo: PlatformInfo;
  isNative: boolean;
  isIOS: boolean;
  isAndroid: boolean;

  // Keyboard
  keyboardVisible: boolean;
  keyboardHeight: number;
  hideKeyboard: () => Promise<void>;

  // Web version modal
  showWebVersionModal: (feature: WebVersionFeature) => void;
  checkFeatureAvailable: (feature: WebVersionFeature) => boolean;

  // Auth handlers (to be called by auth context)
  handleMobileLogin: (
    userId: string,
    accessToken: string,
    refreshToken?: string,
    userData?: any,
  ) => Promise<void>;
  handleMobileLogout: () => Promise<void>;
}

const MobileContext = createContext<MobileContextValue | null>(null);

interface MobileProviderProps {
  children: ReactNode;
}

export function MobileProvider({ children }: MobileProviderProps) {
  const router = useRouter();
  // Initialize with web defaults, then update on client mount
  const [platformInfo, setPlatformInfo] = useState<PlatformInfo>({
    isNative: false,
    isIOS: false,
    isAndroid: false,
    isWeb: true,
    platform: 'web',
  });
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [webModalOpen, setWebModalOpen] = useState(false);
  const [webModalFeature, setWebModalFeature] = useState<WebVersionFeature>('subscription');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Update platform info after mount (client-side only)
  // Also set a cookie for server-side platform detection (used by middleware for routing)
  useEffect(() => {
    const info = getPlatformInfo();
    setPlatformInfo(info);
    setIsMounted(true);
    console.log('[MobileProvider] Platform detected:', info);

    // Stamp the document so plain CSS can be platform-aware (toast viewport
    // repositioning in globals.css keys off html.native-app). A class beats
    // per-component JS checks for anything that portals to <body>.
    if (info.isNative) {
      try { document.documentElement.classList.add('native-app'); } catch { /* noop */ }
    }

    // Set platform cookie for server-side detection
    // This enables middleware to route to mobile-specific pages
    if (info.isNative) {
      document.cookie = 'insura-platform=native; path=/; max-age=31536000; SameSite=Lax';
      console.log('[MobileProvider] Set platform cookie: native');
    } else {
      // Clear the cookie if not native (user might be testing on web)
      document.cookie = 'insura-platform=; path=/; max-age=0';
    }
  }, []);

  // Initialize mobile features
  useEffect(() => {
    // Wait for client-side mount before checking platform
    if (!isMounted) {
      return;
    }

    // If not native, nothing to initialize
    // Note: MobileSplashScreen handles hiding the native splash on native platforms
    if (!platformInfo.isNative) {
      return;
    }

    if (isInitialized) {
      return;
    }

    const initMobile = async () => {
      try {
        // Configure status bar for edge-to-edge display
        // This ensures the status bar overlays the web content with dark text on white
        await setStatusBarOverlay(true);
        await setStatusBarStyle('dark');

        // Initialize keyboard handling
        await initializeKeyboard({
          onShow: (height) => {
            setKeyboardHeight(height);
            setKeyboardVisible(true);
          },
          onHide: () => {
            setKeyboardHeight(0);
            setKeyboardVisible(false);
          },
        });

        // Initialize deep links
        const routes = getInsuraCentralRoutes(router);
        await initializeDeepLinks(routes, (url) => {
          // Handle unmatched deep links
          console.log('Unhandled deep link:', url);
        });

        // Check if app was launched from a deep link
        const launchUrl = await getLaunchUrl();
        if (launchUrl) {
          console.log('App launched from deep link:', launchUrl);
          // Handle the launch URL
        }

        // NOTE: Push notifications and ATT (App Tracking Transparency) are NOT
        // requested here on app init. They are requested AFTER login via
        // runPostLoginPermissionFlow(). This is required for App Store compliance.
        // See: handleMobileLogin() which triggers the post-login permission flow.

        // Initialize mobile auth with dynamically created API client
        try {
          const apiClient = await getAxiosClient();
          await initializeMobileAuth({
            apiClient,
            onDeepLink: (url) => {
              // Navigate to the deep link path
              try {
                const urlObj = new URL(url);
                router.push(urlObj.pathname + urlObj.search);
              } catch {
                router.push(url);
              }
            },
            onSessionExpired: () => {
              // Handle session expiration
              router.push('/auth/sign-in?expired=true');
            },
          });
        } catch (apiError) {
          console.error('Error creating API client for mobile auth:', apiError);
        }

        setIsInitialized(true);
        console.log('Mobile provider initialized');
        // Note: MobileSplashScreen handles hiding the native splash
      } catch (error) {
        console.error('Error initializing mobile features:', error);
        // MobileSplashScreen will still handle the splash
      }
    };

    initMobile();

    return () => {
      cleanupMobileAuth();
    };
  }, [isMounted, platformInfo.isNative, isInitialized, router]);

  // Web version modal handlers
  const showWebVersionModal = useCallback((feature: WebVersionFeature) => {
    console.log('[MobileProvider] showWebVersionModal called with feature:', feature);
    setWebModalFeature(feature);
    setWebModalOpen(true);
    console.log('[MobileProvider] Modal state set to open');
  }, []);

  const checkFeatureAvailable = useCallback(
    (feature: WebVersionFeature): boolean => {
      if (platformInfo.isNative) {
        showWebVersionModal(feature);
        return false;
      }
      return true;
    },
    [platformInfo.isNative, showWebVersionModal],
  );

  // Mobile login handler
  const handleMobileLogin = useCallback(
    async (
      userId: string,
      accessToken: string,
      refreshToken?: string,
      userData?: any,
    ) => {
      if (platformInfo.isNative) {
        await onMobileLogin(userId, accessToken, refreshToken, userData);

        // Run post-login permission flow (push notifications + ATT)
        // This is the App Store compliant way to request permissions
        if (!hasCompletedPostLoginPermissions()) {
          runPostLoginPermissionFlow({
            onBeforePushPrompt: () => {
              console.log('Showing push notification permission prompt...');
            },
            onAfterPushPrompt: (granted) => {
              console.log('Push notification permission:', granted ? 'granted' : 'denied');
            },
            onBeforeATTPrompt: () => {
              console.log('Showing ATT permission prompt...');
            },
            onAfterATTPrompt: (granted) => {
              console.log('ATT permission:', granted ? 'granted' : 'denied');
            },
          }).catch((error) => {
            console.error('Error in post-login permission flow:', error);
          });
        } else {
          // Flow already completed (possibly under the old ATT version) — the
          // analytics question still self-heals: asks once if never answered.
          ensureAnalyticsConsent().catch(() => {});
        }
      }
    },
    [platformInfo.isNative],
  );

  // Mobile logout handler
  const handleMobileLogout = useCallback(async () => {
    if (platformInfo.isNative) {
      await onMobileLogout();
    }
  }, [platformInfo.isNative]);

  const value: MobileContextValue = {
    platformInfo,
    isNative: platformInfo.isNative,
    isIOS: platformInfo.isIOS,
    isAndroid: platformInfo.isAndroid,
    keyboardVisible,
    keyboardHeight,
    hideKeyboard,
    showWebVersionModal,
    checkFeatureAvailable,
    handleMobileLogin,
    handleMobileLogout,
  };

  // Debug log for modal state
  console.log('[MobileProvider] Render - webModalOpen:', webModalOpen, 'feature:', webModalFeature);

  return (
    <MobileContext.Provider value={value}>
      {children}
      <WebVersionModal
        open={webModalOpen}
        onOpenChange={setWebModalOpen}
        feature={webModalFeature}
      />
    </MobileContext.Provider>
  );
}

/**
 * Hook to access mobile context
 */
export function useMobile(): MobileContextValue {
  const context = useContext(MobileContext);
  console.log('[useMobile] Context available:', !!context);
  if (!context) {
    console.log('[useMobile] No context, returning defaults');
    // Return a default value for non-mobile context
    return {
      platformInfo: {
        isNative: false,
        isIOS: false,
        isAndroid: false,
        isWeb: true,
        platform: 'web',
      },
      isNative: false,
      isIOS: false,
      isAndroid: false,
      keyboardVisible: false,
      keyboardHeight: 0,
      hideKeyboard: async () => {},
      showWebVersionModal: () => {},
      checkFeatureAvailable: () => true,
      handleMobileLogin: async () => {},
      handleMobileLogout: async () => {},
    };
  }
  return context;
}

/**
 * Hook to check if running on mobile
 */
export function useIsNative(): boolean {
  const { isNative } = useMobile();
  return isNative;
}

/**
 * Hook to check feature availability
 */
export function useCheckFeature() {
  const { checkFeatureAvailable } = useMobile();
  return checkFeatureAvailable;
}
